import { ProductType } from './product.model';
import { ID } from './ID.model';
import { Property } from './property.model';
import { PaymentMethod } from './payments.model';
import { CategoryV2 } from '../components/generics/data-printer-v2/data-printer-v2.component';
import { UtilsService } from '../services/utils.service';

export type QuoteType = 'COMERCIAL' | 'VIVIENDA';
export type QuoteDiscountType = 'NORMAL' | 'DESCUENTO' | 'EXCEPCION';
export type QuoteUpdateType = 'MANUAL' | 'IPC' | 'ICL' | 'OTRO';

export type PaymentOptionType = 'TRANSFERENCIA' | 'CREDITO' | 'DEBITO' | 'EFECTIVO';

interface AvailablePaymentMethod {
    id: PaymentMethod;
    codigo: string;
    descripcion: string;
    habilitado: boolean;
}

export interface QuoteOption {
    cuotas: number; // Cantidad de cuotas de esta opción
    montoPorCuota: number; // Monto de cada cuota
    montoTotal: number; // Precio final de esta opción (P3 + coeficiente del medio + costos propios)
    montoConDescuentoProducto?: number; // P1, único para toda la cotización (mismo valor en todas las opciones)
    montoConDescuentoComercial?: number; // P3, único para toda la cotización (mismo valor en todas las opciones)
    precioLista?: number; // P. Lista: precio de Crédito 6 cuotas sin Descuento Comercial. Único para toda la cotización.
    tasa?: number; // Dto. Mostrado = 1 - (montoTotal/precioLista), en %. Puede ser negativo (el costo del medio supera al descuento). Siempre presente, incluso en 0%.
    mediosDePago: AvailablePaymentMethod[];
    habilitada: boolean;
    codigo: string;
    tipo: PaymentOptionType;
}

export interface QuotationService {
    id?: ID;
    codigo: string;
    nombre?: string;
    descripcion?: string;
    monto?: number;
    topeMaximo?: number;
    valorC?: number;
}

export interface Quote {
    id?: ID;
    actualizacion?: number;
    alquiler: number;
    creadoPor?: string;
    expensas: number;
    fechaCreacion?: string;
    idAgente?: ID;
    nombreAgente?: string;
    idOficina?: ID;
    opciones?: QuoteOption[];
    periodoActualizacion?: number;
    tipo: QuoteType;
    tipoActualizacion?: QuoteUpdateType;
    tipoDescuento?: QuoteDiscountType;
    codigoMonedaAlquiler?: string;
    codigoMonedaExpensas?: string;
    idPropiedad?: ID;
    propiedad?: Property;
    servicios?: QuotationService[];
    pricingAlquiler?: number;
    pricingExpensas?: number;
    duracion: number;
    idProducto?: ID;
    tipoProducto?: ProductType;
    nombre?: string;
    apellido?: string;
    dniCuit?: string;
    idZona?: number;
    descuentoComercial?: number;
    tipoCambio?: number;
    idComercial?: number;
    perfilAprobado?: boolean;
    // Si la tarjeta de Crédito 12 cuotas se incluye en el PDF (siempre se calcula, es solo de presentación)
    incluir12Cuotas?: boolean;
    // Comisión del comercializador (% de TipoFianza.comision sobre el Precio Final de Transferencia). No va al PDF.
    comisionPct?: number;
    comisionMonto?: number;
}


export function generateQuotationCategories(quotation: Quote, utilsService: UtilsService) {
    if (!quotation) return [];

    const categories: CategoryV2[] = [];

    if (quotation.tipo === 'VIVIENDA') {
        categories.push({
            labelWidth: '70px',
            maxWidth: '450px',
            name: 'Información general',
            items: [
                { label: 'Alquiler', value: quotation.alquiler, type: 'money' },
                { label: 'Expensas', value: quotation.expensas, type: 'money' },
                { label: 'Plazo', value: quotation.duracion + ' meses' },
            ]
        });
    } else if (quotation.tipo === 'COMERCIAL') {
        categories.push({
            labelWidth: '190px',
            maxWidth: '450px',
            name: 'Información general',
            items: [
                { label: 'Alquiler', value: quotation.alquiler, type: 'money' },
                { label: 'Expensas', value: quotation.expensas, type: 'money' },
                { label: 'Porcentaje de actualización', value: quotation.actualizacion, type: 'percent' },
                { label: 'Periodo de actualización', value: quotation.periodoActualizacion ? quotation.periodoActualizacion + ' meses' : undefined },
                { label: 'Plazo', value: quotation.duracion + ' meses' },
            ]
        });
    }

    if (quotation.servicios?.length) {
        categories.push({
            maxWidth: '250px',
            name: 'Servicios',
            items: [{ value: quotation.servicios.map(service => utilsService.beautifyText(service.nombre)).join(', '), alignment: 'center', type: 'text' }]
        });
    }

    if (quotation.opciones?.filter(opcion => opcion.tipo === 'EFECTIVO')?.length) {
        categories.push({
            maxWidth: '450px',
            name: 'Opciones de pago (efectivo)',
            items: quotation.opciones.filter(opcion => opcion.tipo === 'EFECTIVO').map(opcion => ({
                value: ` ${ utilsService.parseByType(opcion.montoPorCuota, 'money') }${ opcion.cuotas > 1 ? ' (' + utilsService.parseByType(opcion.montoTotal, 'money') + ')' : '' }`,
                alignment: 'center',
                type: 'text'
            }))
        });
    }

    if (quotation.opciones?.filter(opcion => opcion.tipo === 'CREDITO')?.length) {
        categories.push({
            maxWidth: '450px',
            name: 'Opciones de pago (tarjeta de crédito)',
            items: quotation.opciones.filter(opcion => opcion.tipo === 'CREDITO').map(opcion => ({
                value: `${ opcion.cuotas } ${ opcion.cuotas > 1 ? 'cuotas' : 'cuota' } de ${ utilsService.parseByType(opcion.montoPorCuota, 'money') }${ opcion.cuotas > 1 ? ' (' + utilsService.parseByType(opcion.montoTotal, 'money') + ')' : '' }`,
                alignment: 'center',
                type: 'text'
            }))
        });
    }

    if (quotation.opciones?.filter(opcion => opcion.tipo === 'DEBITO')?.length) {
        categories.push({
            maxWidth: '450px',
            name: 'Opciones de pago (tarjeta de débito)',
            items: quotation.opciones.filter(opcion => opcion.tipo === 'DEBITO').map(opcion => ({
                value: `${ opcion.cuotas } ${ opcion.cuotas > 1 ? 'cuotas' : 'cuota' } de ${ utilsService.parseByType(opcion.montoPorCuota, 'money') }${ opcion.cuotas > 1 ? ' (' + utilsService.parseByType(opcion.montoTotal, 'money') + ')' : '' }`,
                alignment: 'center',
                type: 'text'
            }))
        });
    }

    if (quotation.opciones?.filter(opcion => opcion.tipo === 'TRANSFERENCIA')?.length) {
        categories.push({
            maxWidth: '450px',
            name: 'Opciones de pago (transferencia)',
            items: quotation.opciones.filter(opcion => opcion.tipo === 'TRANSFERENCIA').map(opcion => ({
                value: `${ utilsService.parseByType(opcion.montoPorCuota, 'money') }${ opcion.cuotas > 1 ? ' (' + utilsService.parseByType(opcion.montoTotal, 'money') + ')' : '' }`,
                alignment: 'center',
                type: 'text'
            }))
        });
    }

    return categories;
}
